import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { mobileDigitsOnly, mobileValidationError } from '@uniqcamp/shared';
import type { AuthMeResponse } from '../types';

const Profile: React.FC = () => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [photo, setPhoto] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const { data: meData, isLoading } = useQuery<AuthMeResponse>('auth-me', () =>
    apiClient.get('/auth/me').then((r: { data: unknown }) => r.data as AuthMeResponse)
  );
  const user = meData?.user;

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setMobile(user.mobile || '');
      setPhoto(user.photo || '');
    }
  }, [user]);

  const updateMutation = useMutation<AuthMeResponse, unknown, { name: string; mobile: string; photo: string | null }>(
    async (payload) => {
      const res = await apiClient.put('/users/me', payload);
      return res.data as AuthMeResponse;
    },
    {
      onSuccess: (data: { user?: { _id?: string; name?: string; mobile?: string; email?: string; role?: string; schoolId?: string; photo?: string } }) => {
        if (data?.user) {
          localStorage.setItem('user', JSON.stringify({
            id: data.user._id,
            name: data.user.name,
            mobile: data.user.mobile,
            email: data.user.email,
            role: data.user.role,
            schoolId: data.user.schoolId,
            photo: data.user.photo,
          }));
        }
        queryClient.invalidateQueries('auth-me');
        setMessage({ type: 'success', text: 'Profile updated.' });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        setMessage({ type: 'error', text: e.response?.data?.message ?? 'Failed to update' });
      },
    }
  );

  const handleSave = () => {
    setMessage({ type: '', text: '' });
    const mobErr = mobileValidationError(mobile, { required: true, label: 'Mobile number' });
    if (mobErr) {
      setMessage({ type: 'error', text: mobErr });
      return;
    }
    updateMutation.mutate({ name: name.trim(), mobile: mobileDigitsOnly(mobile), photo: photo.trim() || null });
  };

  if (isLoading) return <Box><CircularProgress /></Box>;
  if (!user) return <Box><Typography>Please log in.</Typography></Box>;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Profile
      </Typography>

      {message.text && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Avatar src={photo} sx={{ width: 80, height: 80 }}>{name?.charAt(0) || 'P'}</Avatar>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Photo URL (paste a link to your photo)
          </Typography>
        </Box>
        <TextField
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Mobile number"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Photo URL"
          value={photo}
          onChange={(e) => setPhoto(e.target.value)}
          placeholder="https://..."
          margin="normal"
        />
        <Button
          fullWidth
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={updateMutation.isLoading}
          sx={{ mt: 2 }}
        >
          Save
        </Button>
      </Paper>
    </Box>
  );
};

export default Profile;
